import { create } from 'zustand';
import { makeRequest } from "../utils/axios.js"; 
import { encryptMessage, decryptMessage } from "../utils/cryptoUtils";

export const useChatDatabase = create((set, get) => ({
    chats: [],
    isCurrentUserBlocked: false,
    isReceiverBlocked: false,
    messages: [],
    isLoadingMessages: false,
    currentChatId: null, 
    currentReceiver: null,
    isDetailOpen: false,

    toggleDetail: () => set((state) => ({ isDetailOpen: !state.isDetailOpen })),

    clearChat: () => {
        set({
            currentChatId: null,
            currentReceiver: null,
            messages: [],
            isCurrentUserBlocked: false,
            isReceiverBlocked: false,
            isDetailOpen: false,
        });
    },

    fetchChatList: async () => {
        try {
            const res = await makeRequest.get("/chats");
            set({ chats: res.data });
        } catch (err) {
            console.log(err);
            set({ chats: [] });
        }
    },

    deleteChat: async (chatId) => {
        try {
            await makeRequest.delete(`/chats/${chatId}`);
            
            set((state) => {
                const updatedChats = state.chats.filter((chat) => chat.chatId !== chatId);
                
                if (String(state.currentChatId) === String(chatId)) {
                    return {
                        chats: updatedChats,
                        currentChatId: null,
                        currentReceiver: null,
                        messages: [],
                        isCurrentUserBlocked: false,
                        isReceiverBlocked: false,
                        isDetailOpen: false,
                    };
                }

                return { chats: updatedChats };
            });
            
        } catch (err) {
            console.log("Error when deleting FE chat:", err);
        }
    },

    selectChat: async (chat, myPrivateKey) => {
        set({
            messages: [],
            isLoadingMessages: true,
            currentChatId: chat.chatId,
            currentReceiver: {
                id: chat.receiverId,
                name: chat.receiverName,
                avatar: chat.receiverProfilePic,
            },
            isCurrentUserBlocked: false,
            isReceiverBlocked: false,
        });

        try {
            const messagesRes = await makeRequest.get(`/chats/${chat.chatId}`);       
            const storedUser = JSON.parse(localStorage.getItem("user"));
            const myUserId = String(storedUser?.id || "");

            const decryptedMessages = await Promise.all(
                messagesRes.data.map(async (msg) => {
                    let encryptedTextToDecode = "";
                    
                    if (String(msg.senderId) === myUserId) {
                        encryptedTextToDecode = msg.senderEncryptedText;
                    } else {
                        encryptedTextToDecode = msg.receiverEncryptedText;
                    }

                    if (!encryptedTextToDecode) return { ...msg, text: "" };
                    
                    try {
                        if (!myPrivateKey) throw new Error("No private key yet");
                        
                        const decodedText = await decryptMessage(encryptedTextToDecode, myPrivateKey);
                        return { ...msg, text: decodedText };
                    } catch (error) {
                        console.error("Decoding error:", error);
                        return { ...msg, text: "[This message cannot be decrypted.]" };
                    }
                })
            );

            if (!chat.isSeen) {
                await makeRequest.put(`/chats/${chat.chatId}/read`);
                get().fetchChatList();
            }

            const resStatus = await makeRequest.get(`/users/blockstatus/${chat.receiverId}`);

            set({
                messages: decryptedMessages, 
                isCurrentUserBlocked: resStatus.data.isCurrentUserBlocked,
                isReceiverBlocked: resStatus.data.isReceiverBlocked,
                isLoadingMessages: false,
            });
        } catch (err) {
            console.log(err);
            set({ isLoadingMessages: false, messages: [] });
        }
    },

    toggleBlock: async () => {
        const receiverId = get().currentReceiver?.id;
        if (!receiverId) return;

        const currentStatus = get().isReceiverBlocked;
        set({ isReceiverBlocked: !currentStatus }); 

        try {
            await makeRequest.post("/users/block", { userIdToBlock: receiverId });
        } catch (err) {
            console.log(err);
            set({ isReceiverBlocked: currentStatus }); 
        }
    },

    startNewChat: (receiver) => {
        set({
            currentChatId: null,
            currentReceiver: {
                id: receiver.id,
                name: receiver.name,
                avatar: receiver.profilePic,
            },
            messages: [],
            isCurrentUserBlocked: false,
            isReceiverBlocked: false,
        });
    },

    sendMessage: async (text, receiverId, files, myPublicKey) => {
        if (!receiverId) return;

        try {
            if (!myPublicKey) throw new Error("Your encryption key (Public Key) was not found. Please log in again.");

            const receiverKeyRes = await makeRequest.get(`/users/publickey/${receiverId}`);
            const receiverPublicKey = receiverKeyRes.data.publicKey;

            let senderEncryptedText = "";
            let receiverEncryptedText = "";

            if (text) {
                senderEncryptedText = await encryptMessage(text, myPublicKey);
                receiverEncryptedText = await encryptMessage(text, receiverPublicKey);
            }

            const formData = new FormData();
            formData.append("senderEncryptedText", senderEncryptedText);
            formData.append("receiverEncryptedText", receiverEncryptedText);
            formData.append("receiverId", receiverId);

            if (files && files.length > 0) {
                files.forEach((file) => {
                    formData.append("files", file); 
                });
            }

            const res = await makeRequest.post("/messages", formData);

            const newMessageForUI = { ...res.data, text: text };

            let fallbackAttachment = "";

            if (!text && files && files.length > 0) {
                const fileType = files[0].type || "";
                let type = "file";
                if (fileType.startsWith("image/")) type = "image";
                else if (fileType.startsWith("video/")) type = "video";
                else if (fileType.startsWith("audio/")) type = "audio";
                fallbackAttachment = `[${type} attached]`;
            }

            set((state) => {
                let updatedChats = [...state.chats];
                const chatIndex = updatedChats.findIndex(c => String(c.receiverId) === String(receiverId));

                if (chatIndex !== -1) {
                    const chatToUpdate = { 
                        ...updatedChats[chatIndex], 
                        lastMessage: senderEncryptedText || fallbackAttachment
                    };
                    updatedChats.splice(chatIndex, 1);
                    updatedChats.unshift(chatToUpdate);
                }

                return {
                    messages: [...state.messages, newMessageForUI],
                    chats: updatedChats
                };
            });

            return res.data;
        } catch (err) {
            console.log("Message sending error (in-store):", err);
            throw err; 
        }
    },

    addIncomingMessage: async (incomingMessage, myPrivateKey) => {
        const currentReceiverId = String(get().currentReceiver?.id);
        const senderId = String(incomingMessage.senderId);

        let textForUI = incomingMessage.text || ""; 

        if (currentReceiverId === senderId && incomingMessage.receiverEncryptedText && myPrivateKey) {
            try {
                textForUI = await decryptMessage(incomingMessage.receiverEncryptedText, myPrivateKey);
            } catch (error) {
                console.error("Message decryption error:", error);
                textForUI = "[This message cannot be decrypted.]";
            }
        }

        const messageForUI = { ...incomingMessage, text: textForUI };

        let fallbackAttachment = "";

        if (!incomingMessage.receiverEncryptedText && incomingMessage.attachments?.length > 0) {
            fallbackAttachment = `[${incomingMessage.attachments[0].fileType} attached]`;
        }

        set((state) => {
            let updatedChats = [...state.chats];
            const chatIndex = updatedChats.findIndex(c => String(c.receiverId) === senderId);

            if (chatIndex !== -1) {
                const chatToUpdate = {
                    ...updatedChats[chatIndex],
                    lastMessage: incomingMessage.receiverEncryptedText || fallbackAttachment
                };

                if (currentReceiverId !== senderId) {
                    chatToUpdate.isSeen = false;
                }
                
                updatedChats.splice(chatIndex, 1);
                updatedChats.unshift(chatToUpdate);
            } else {
                setTimeout(() => {
                    get().fetchChatList();
                }, 500); 
            }

            let newMessages = state.messages;
            if (currentReceiverId === senderId) {
                newMessages = [...state.messages, messageForUI];
            }

            return {
                chats: updatedChats,
                messages: newMessages
            };
        });

        if (currentReceiverId === senderId && get().currentChatId) {
            makeRequest.put(`/chats/${get().currentChatId}/read`).catch(err => {
                console.error("Error updating read status:", err);
            });
        }
    }
}));